import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AuditDialog } from '../../../models';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ApplicationService, CyberSecurityOrgService } from '@pt/api-client';
import { ColDef, GridApi, GridOptions, GridReadyEvent, RowClickedEvent } from 'ag-grid-community';
import { PRIMATOO_BASE_PROD_ENVIRONMENT } from '@pt/pt-core';
import { AG_GRID_LOCALE_EN, IoniconsFilled, IoniconsOutline } from '@pt/pt-models';
import {
  ag_grid_frameworkComponents,
  AgGridTableWrapper,
  Confirm,
  CveDetailsRendererComponent,
  DialogService
} from '@pt/pt-shared';
import { forkJoin, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ListUserColumnsResponse } from 'it/primatoo/openapi/api/org/schemas/common/ListUserColumnsResponse';
import { PenTestUtils } from '../pen-test-dialog/pen-test-utils';
import { ColumnWithLocalization } from 'it/primatoo/openapi/api/org/schemas/common/ColumnWithLocalization';
import { TranslateService } from '@ngx-translate/core';
import { AppAuditCve } from 'it/primatoo/openapi/api/org/schemas/cybersecurity/AppAuditCve';
import { CVEAudit } from 'it/primatoo/openapi/api/org/schemas/cybersecurity/_appauditcve/CVEAudit';
import { ImpactMetricValue } from 'it/primatoo/openapi/api/org/schemas/common/ImpactMetricValue';

@Component({
  selector: 'pt-audit-dialog',
  templateUrl: './audit-dialog.component.html',
  styleUrls: ['audit-dialog.component.scss']
})
export class AuditDialogComponent implements OnInit, OnDestroy {
  @Input() auditDialog: AuditDialog;
  @ViewChild('vulnerabilityCol', {static: true}) vulnerabilityCol: TemplateRef<unknown>;

  public detailCellRenderer: any = CveDetailsRendererComponent;
  public IconWarning = IoniconsOutline.warning;

  private gridApi!: GridApi;
  public impactMetricValues: Array<ImpactMetricValue> = [];
  public impactMetricsLabels: Array<string> = [];

  public appAuditCve?: AppAuditCve;
  public gridOption!: GridOptions;
  public loading = false;
  public enabledMarkAll = false;
  private appAuditColumns: ColumnWithLocalization[];
  private columnDefs: ColDef[];
  public IconClose = IoniconsFilled.close;

  public IconReload = IoniconsFilled.refresh;
  private changedRowIndex: number[] = [];
  public IconEdit = IoniconsOutline.edit;
  private destroy$ = new Subject<boolean>();

  private isChanged = false;

  constructor(public activeModal: NgbActiveModal,
              private translateService: TranslateService,
              private dialogService: DialogService,
              private applicationService: ApplicationService,
              private cyberSecService: CyberSecurityOrgService) {
  }

  ngOnInit(): void {
    if (this.auditDialog) {
      this.isChanged = false;
      this.load(this.auditDialog.applicationId);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  public reload() {
    this.load(this.auditDialog.applicationId);
  }

  openUrl(url: string) {
    if (url) {
      window.open(url, '_blank');
    }
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
  }

  private load(applicationId: number) {
    this.loading = true;
    forkJoin({
      appAuditCve: this.cyberSecService.getAppAuditCve(applicationId),
      appAuditColumns: this.cyberSecService.getAppAuditColumns()
    }).pipe(takeUntil(this.destroy$))
      .subscribe((responses: {
        appAuditCve: AppAuditCve,
        appAuditColumns: ListUserColumnsResponse
      }) => {
        this.appAuditCve = responses.appAuditCve;
        this.impactMetricValues = this.appAuditCve.impactMetrics;
        this.impactMetricsLabels = this.appAuditCve.impactMetricsLabels;
        this.appAuditColumns = responses.appAuditColumns.columns;
        this.enabledMarkAll = !!responses.appAuditCve.cves?.find(c => c.toCheck);
        this.alignTable();
      });
  }

  private alignTable() {
    if (this.appAuditCve && this.appAuditColumns) {
      this.changedRowIndex = [];
      this.loading = true;
      const rowData: Array<CVEAudit> = this.appAuditCve.cves;
      const columns: Array<ColumnWithLocalization> = this.appAuditColumns;
      this.columnDefs = this.generateColumns(columns);
      const impactMetricsLabels: Array<{
        value: number,
        label: string
      }> = this.appAuditCve.impactMetricsLabels.map((v, k) => {
        return {value: k, label: v};
      });

      setTimeout(() => {
        this.gridOption = {
          ...PRIMATOO_BASE_PROD_ENVIRONMENT.defaultGridOptions,
          rowData,
          context: {
            applicationId: this.auditDialog.applicationId,
            threatEval: impactMetricsLabels,
            columnsFields: ['confidentialityViolation', 'integrityCompromise', 'availabilityLoss'],
            reloadThreats: () => {
              this.reloadImpactMetrics();
            },
            isChanged: () => {
              this.isChanged = true;
            }
          },
          columnDefs: this.columnDefs,
          localeText: AG_GRID_LOCALE_EN,
          suppressPropertyNamesCheck: true,
          components: ag_grid_frameworkComponents,
          rowBuffer: 5,
          pinnedBottomRowData: [this.appAuditCve?.cves?.length || 0],
          fullWidthCellRenderer: 'footerRenderComponent',
          isFullWidthRow: (params) => params.rowNode.rowPinned === 'bottom',
          getContextMenuItems: () => PRIMATOO_BASE_PROD_ENVIRONMENT.defaultContextMenuItems,
          getRowStyle: (params) => {
            const data = params.data as CVEAudit;
            if (data.suppressed)
              return {"opacity": "60%", "background-color": "#e4e4e4"};
            return {};
          }
        } as GridOptions;
        this.loading = false;
      }, 200);
    }
  }

  private reloadImpactMetrics() {
    this.cyberSecService.getAppAudit(this.auditDialog.applicationId).subscribe(s => {
      this.impactMetricValues = s.impactMetrics;
      this.impactMetricsLabels = s.impactMetricsLabels;
    });
  }

  private generateColumns(columns: Array<ColumnWithLocalization>): ColDef[] {
    const response: ColDef[] = [];
    response.push({headerName: '', cellRenderer: 'agGroupCellRenderer', width: 50});
    columns.forEach((column) => {
      let col: ColDef;
      switch (column.column) {
        case 'vulnerability':
          col = PenTestUtils.colDefCustom(this.vulnerabilityCol, column.label, false, 150); //  this.editMode);
          break;
        case 'severity':
          col = PenTestUtils.colDefSeverity(column, false);
          break;
        case 'confidentialityViolation':
        case 'integrityCompromise':
        case 'availabilityLoss':
          col = PenTestUtils.colDefThreat(column, false); // this.editMode);
          col.cellStyle = params => {
            let color = 'transparent';
            if (!PenTestUtils.isValidNumberAndNotZero(params.value) && this.changedRowIndex.includes(params.rowIndex)) {
              color = PenTestUtils.INVALID_COLOR;
            }
            return {
              backgroundColor: color
            };
          };
          break;
        default:
          col = AgGridTableWrapper.generateColDef(column);
      }
      response.push(col);
    });
    // response.push(PenTestUtils.colDefAction(this.actionCol, 100))
    return response;
  }

  @Confirm({
    title: 'TITLE.REMOVE_VULNERABILITY',
    message: 'MESSAGE.REMOVE_VULNERABILITY'
  })
  removeSecurityToolType() {
    this.applicationService.updateSecurityConfig(this.auditDialog.applicationId, {
      securityCfgType: null,
      vendorProduct: null
    }).subscribe({
      next: () => {
        this.isChanged = true;
        this.close();
      }, error: () => {
        this.close();
      }
    });
  }


  close() {
    this.activeModal.close(this.isChanged);
    this.destroy$.next(true);
  }

  expandRow($event: RowClickedEvent<any>) {
    if (this.gridApi) {
      this.gridApi.forEachNode(function (node) {
        if (node.rowIndex === $event.rowIndex) {
          if (!node.expanded) {
            node.setExpanded(true);
          }
        } else {
          if (node.expanded) {
            node.setExpanded(false);
          }
        }
      });
    }
  }

  openNote() {
    const note = this.appAuditCve?.note;
    const applicationId = this.auditDialog.applicationId;
    const saveBtnLabel: Observable<string> = this.translateService.stream('BUTTON.SAVE');
    const dialog = this.dialogService.runTextArea(this.translateService.stream('TITLE.NOTES'), note, true, saveBtnLabel);
    dialog.subscribe((value) => {
      if (note !== value) {
        this.cyberSecService.patchAppAuditNote(applicationId, {note: value})
          .pipe(takeUntil(this.destroy$))
          .subscribe((s) => {
            this.isChanged = true;
            this.appAuditCve.note = value;
          }, () => {
            this.reload();
          });
      }
    });
  }
}

